// Primed-TNT render (M15.2.2): a pooled cube per primed entity, tinted the TNT
// map colour and flashing white as the fuse burns (Java: it blinks, quickening
// near detonation). Client-only (ENG-1); the sim owns the physics + fuse.
import * as THREE from 'three';
import type { PrimedTnt } from '../../sim/entities/tnt';

const TNT_RED = 0xdb441a;
const FLASH = 0xffffff;

export class PrimedTntRenderer {
  private readonly group = new THREE.Group();
  private readonly meshes = new Map<number, THREE.Mesh>();
  private readonly geo = new THREE.BoxGeometry(0.98, 0.98, 0.98);

  constructor(scene: THREE.Scene) {
    scene.add(this.group);
  }

  update(primed: readonly PrimedTnt[]): void {
    const seen = new Set<number>();
    for (const t of primed) {
      seen.add(t.id);
      let m = this.meshes.get(t.id);
      if (!m) {
        m = new THREE.Mesh(this.geo, new THREE.MeshBasicMaterial({ color: TNT_RED }));
        this.meshes.set(t.id, m);
        this.group.add(m);
      }
      // The sim stores the block bottom at `y`; the box is centred, so + 0.5.
      m.position.set(t.x, t.y + 0.5, t.z);
      // Blink white on alternate ~5-tick phases so it reads as "primed".
      (m.material as THREE.MeshBasicMaterial).color.setHex(
        Math.floor(t.age / 5) % 2 === 0 ? FLASH : TNT_RED,
      );
    }
    for (const [id, m] of this.meshes) {
      if (seen.has(id)) continue;
      this.group.remove(m);
      (m.material as THREE.Material).dispose();
      this.meshes.delete(id);
    }
  }

  clear(): void {
    for (const m of this.meshes.values()) {
      this.group.remove(m);
      (m.material as THREE.Material).dispose();
    }
    this.meshes.clear();
  }
}
